import { observer } from "mobx-react-lite";
import { usePipedriveStore, usePopupStore } from "../..";
import useNavigateToDeal from "../../hooks/useNavigateToDeal";
import IDeal from "../../interfaces/IDeal";
import PopupType from "../../interfaces/PopupType";
import { dealStatusToString } from "../../utils/enumUtils";
import { formatNumber, formatPrice } from "../../utils/utils";
import commonStyles from "../Common.module.css";
import Grid, { IHeader } from "../Grid";

function DealsGrid<T extends IDeal>({ deals, additionalHeadersBefore, additionalHeadersAfter, radiusDropdown }: IDealsGridProps<T>) {
    const navigateToDeal = useNavigateToDeal();
    const pipedriveStore = usePipedriveStore();
    const popupStore = usePopupStore();

    return (
        <Grid
            data={deals}
            headers={[
                ...additionalHeadersBefore || [],
                {
                    align: "left",
                    titleStrings: ["NAME"],
                    getValue: item => item.name,
                    getLabel: item => item.name,
                },
                {
                    titleStrings: ["VALUE"],
                    getValue: item => item.value,
                    getLabel: item => !!item.value ? formatPrice(item.value) : "",
                },
                {
                    align: "left",
                    titleStrings: ["STAGE"],
                    getValue: item => pipedriveStore.dealStages.find(s => s.id === item.dealStageId)?.name,
                    getLabel: item => pipedriveStore.dealStages.find(s => s.id === item.dealStageId)?.name || "",
                },
                {
                    titleStrings: ["STATUS"],
                    getValue: item => item.status,
                    getLabel: item => dealStatusToString(item.status)!,
                },
                {
                    titleStrings: ["BUILDING", "SIZE"],
                    getValue: item => item.buildingSize,
                    getLabel: item => item.buildingSize ? formatNumber(item.buildingSize) : "",
                },
                {
                    titleStrings: ["LAND", "SIZE"],
                    getValue: item => item.landSizeAcres,
                    getLabel: item => item.landSizeAcres ? formatNumber(item.landSizeAcres) : "",
                },
                {
                    titleStrings: ["USABLE", "LAND SIZE"],
                    getValue: item => item.usableLand,
                    getLabel: item => item.usableLand ? formatNumber(item.usableLand) : "",
                },
                {
                    titleStrings: ["MARKET RENT", "(BUILDING)"],
                    getValue: item => item.marketRentBuilding,
                    getLabel: item => item.marketRentBuilding ? formatPrice(item.marketRentBuilding) : "",
                },
                {
                    titleStrings: ["MARKET RENT", "(LAND)"],
                    getValue: item => item.marketRentLand,
                    getLabel: item => item.marketRentLand ? formatPrice(item.marketRentLand) : "",
                },
                {
                    titleStrings: ["ZONING"],
                    getValue: item => item.zoning,
                    getLabel: item => item.zoning || "",
                },
                ...additionalHeadersAfter || [],
            ]}
            getId={item => item.id}
            downloadFileName={"Deals"}
            onRowClick={item => navigateToDeal(item.id)}
            isRowActive={item => popupStore.type === PopupType.Deal && popupStore.id === item.id}
            header={<div className={[commonStyles.gridTitle, commonStyles.header].join(" ")}>
                {deals.length} Deals
                {radiusDropdown}
            </div>}
        />
    );
}

export default observer(DealsGrid);

interface IDealsGridProps<T extends IDeal> {
    deals: T[];
    additionalHeadersBefore?: IHeader<T>[];
    additionalHeadersAfter?: IHeader<T>[];
    radiusDropdown?: React.ReactNode;
}